import { useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

interface PdfDocumentViewProps {
    data: Uint8Array;
    onError?: () => void;
    onReady?: () => void;
}

const PdfDocumentView = ({ data, onError, onReady }: PdfDocumentViewProps) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const onErrorRef = useRef(onError);
    const onReadyRef = useRef(onReady);
    const [failed, setFailed] = useState(false);
    const [visible, setVisible] = useState(false);
    onErrorRef.current = onError;
    onReadyRef.current = onReady;

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;

        let cancelled = false;
        setFailed(false);
        setVisible(false);
        const loadingTask = getDocument({ data: data.slice() });

        const render = async () => {
            const pdf = await loadingTask.promise;
            if (cancelled) return;
            host.replaceChildren();
            const width = Math.max(host.clientWidth, 320);
            const pixelRatio = window.devicePixelRatio || 1;

            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                if (cancelled) return;
                const page = await pdf.getPage(pageNumber);
                const unscaled = page.getViewport({ scale: 1 });
                const scale = (width / unscaled.width) * pixelRatio;
                const viewport = page.getViewport({ scale });
                const canvas = document.createElement('canvas');
                canvas.className = 'proposal-pdf-preview-page';
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
                canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;
                const context = canvas.getContext('2d');
                if (!context) continue;
                host.appendChild(canvas);
                await page.render({ canvasContext: context, viewport }).promise;
            }
            if (cancelled) return;
            setVisible(true);
            onReadyRef.current?.();
        };

        render().catch(() => {
            if (cancelled) return;
            setFailed(true);
            onErrorRef.current?.();
        });

        return () => {
            cancelled = true;
            void loadingTask.destroy();
        };
    }, [data]);

    if (failed) return null;

    return (
        <div
            ref={hostRef}
            className="proposal-pdf-preview"
            style={visible ? undefined : { position: 'absolute', width: '100%', visibility: 'hidden' }}
        />
    );
};

export default PdfDocumentView;
